import JSZip from 'jszip'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildCanvasDownloadFilename } from '~/components/canvas/canvasDocumentMeta'
import {
  CANVAS_HTML_SANDBOX,
  downloadRenderedCanvasHtmlPdf,
  openSandboxedCanvasHtmlInNewTab
} from '~/components/canvas/canvasHtmlExport'
import {
  SelectorDropdownContent,
  SelectorDropdownItem
} from '~/components/common/dropdowns/SelectorDropdown'
import { DownloadProgressButton } from '~/components/common/dropdowns/DownloadProgressButton'
import {
  base64ToBlob,
  createDataUri,
  ensureFileExtension,
  estimateBase64Size,
  FileItem,
  FileTree,
  FileViewerItem,
  getMimeType,
  isHtmlMimeType,
  isImageMimeType,
  isMarkdownMimeType,
  MAX_RENDERABLE_SIZE
} from '~/components/common/FileTree'
import { HoverText } from '~/components/common/HoverText'
import {
  SegmentControls,
  SegmentControlsList,
  SegmentControlsTrigger
} from '~/components/common/SegmentControls'
import DocMarkdown from '~/components/documents/DocMarkdown'
import { getLanguageFromFilename, HighlightedCode } from '~/components/documents/HighlightedCode'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { getCanvasFileContent } from '~/services/canvasService'
import {
  BINARY_EXTENSIONS,
  extractOriginalFilename,
  extractTarGz,
  type ExtractedFile
} from '~/utils/tarUtils'
import { BetterToast } from '~/utils/toast'
import type { CanvasItem } from './message'

export interface CanvasFileViewerProps {
  canvasItems: CanvasItem[]
  initialSelectedIndex?: number
  onClose?: () => void
  className?: string
  sidebarTitle?: string
}

interface FileContent {
  content: string
  isLoading: boolean
  error?: string
  viewerItem?: FileViewerItem
}

/**
 * Represents a processed file item that can be either:
 * - An original CanvasItem (non-tar.gz)
 * - An extracted file from a tar.gz archive
 */
interface ProcessedFileItem {
  /** File path for display (with directory structure) */
  filePath: string
  /** Original canvas item (for non-extracted files) */
  canvasItem?: CanvasItem
  /** Extracted file data (for files from tar.gz) */
  extractedFile?: ExtractedFile
  /** Index in the processed items array */
  index: number
  /** Error message if extraction failed */
  extractionError?: string
}

/**
 * Check if a filename indicates a tar.gz file
 */
function isTarGzFile(filename: string): boolean {
  return filename.toLowerCase().endsWith('.tar.gz')
}

/**
 * Get the file extension for a canvas item based on canvas type.
 * Extension detection hierarchy:
 * 1. CANVAS_TYPE_IMAGE → 'png'
 * 2. CANVAS_TYPE_MARKDOWN → 'md'
 * 3. CANVAS_TYPE_S3_FILE → extract from s3FilePath only (never from title)
 */
function getCanvasExtension(canvas: CanvasItem): string {
  switch (canvas.canvasType) {
    case 'CANVAS_TYPE_IMAGE':
      return 'png'
    case 'CANVAS_TYPE_MARKDOWN':
      return 'md'
    case 'CANVAS_TYPE_S3_FILE':
    default:
      if (canvas.s3FilePath) {
        const actualFilename = extractOriginalFilename(canvas.s3FilePath)
        return actualFilename.split('.').pop()?.toLowerCase() || ''
      }
      return ''
  }
}

/**
 * Check if a canvas item is a tar.gz file based on canvas type and s3FilePath
 * Only checks s3FilePath for CANVAS_TYPE_S3_FILE, never the title
 */
function isCanvasItemTarGz(canvas: CanvasItem): boolean {
  if (canvas.canvasType !== 'CANVAS_TYPE_S3_FILE') {
    return false
  }
  if (canvas.s3FilePath) {
    return isTarGzFile(canvas.s3FilePath)
  }
  return false
}

/**
 * Extract basename from a path (handles both S3 paths and regular filenames).
 */
function getBasename(pathOrFilename: string): string {
  // Handle S3 paths like "workspace/uuid/files/image.png" -> "image.png"
  const parts = pathOrFilename.split('/')
  return parts[parts.length - 1] || pathOrFilename
}

/**
 * Normalize a CanvasItem to a FileViewerItem for consistent handling.
 * This is called after fetching content (lazy normalization).
 *
 * Extension detection hierarchy (never uses title):
 * - CANVAS_TYPE_IMAGE: always png
 * - CANVAS_TYPE_MARKDOWN: always md
 * - CANVAS_TYPE_S3_FILE: extract from s3FilePath only
 */
function normalizeToFileViewerItem(canvas: CanvasItem, content: string): FileViewerItem {
  // Get display filename from title, fallback to basename of s3FilePath
  const rawFilename = canvas.title
    ? getBasename(canvas.title)
    : canvas.s3FilePath
      ? getBasename(canvas.s3FilePath)
      : 'untitled'

  let contentEncoding: 'text' | 'base64'
  let mimeType: string

  switch (canvas.canvasType) {
    case 'CANVAS_TYPE_IMAGE':
      // Images are always base64 encoded PNGs
      contentEncoding = 'base64'
      mimeType = 'image/png'
      break

    case 'CANVAS_TYPE_MARKDOWN':
      // Markdown is text content
      contentEncoding = 'text'
      mimeType = 'text/markdown'
      break

    case 'CANVAS_TYPE_S3_FILE':
    default: {
      // For S3 files, get extension from s3FilePath only (never from title)
      const ext = getCanvasExtension(canvas)
      mimeType = getMimeType(ext ? `file.${ext}` : rawFilename)

      // Binary file types need base64 encoding
      contentEncoding = BINARY_EXTENSIONS.has(ext) ? 'base64' : 'text'
      break
    }
  }

  // Ensure filename has correct extension based on mimeType
  const filename = ensureFileExtension(rawFilename, mimeType)

  // Estimate size for large file detection
  const size = contentEncoding === 'base64' ? estimateBase64Size(content) : new Blob([content]).size

  const isTooLarge = size > MAX_RENDERABLE_SIZE

  return {
    filename,
    content,
    contentEncoding,
    mimeType,
    size,
    isTooLarge
  }
}

/**
 * File viewer modal for canvas items (generated files/artifacts).
 * Uses the common FileTree component and supports markdown and image display.
 * Automatically expands tar.gz files to show their contents.
 */
export function CanvasFileViewer({
  canvasItems,
  initialSelectedIndex = 0,
  onClose,
  className,
  sidebarTitle = 'Generated Files'
}: CanvasFileViewerProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex)
  const [selectedPath, setSelectedPath] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState<FileContent>({
    content: '',
    isLoading: false
  })
  const [sidebarWidth, setSidebarWidth] = useState(300)
  const [isResizing, setIsResizing] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const sidebarRef = useRef<HTMLDivElement>(null)
  const downloadRef = useRef<HTMLDivElement>(null)
  const htmlPreviewFrameRef = useRef<HTMLIFrameElement | null>(null)
  const lastHtmlPreviewWidthRef = useRef<number>()
  const [isDownloadOpen, setIsDownloadOpen] = useState(false)

  const [htmlViewMode, setHtmlViewMode] = useState<'rendered' | 'source'>('rendered')

  // State for tar.gz expansion
  const [processedItems, setProcessedItems] = useState<ProcessedFileItem[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingError, setProcessingError] = useState<string | null>(null)

  const MIN_SIDEBAR_WIDTH = 200
  const MAX_SIDEBAR_WIDTH = 600

  // Process canvas items and expand tar.gz files
  useEffect(() => {
    if (!currentWorkspace?.uuid) return

    const processCanvasItems = async () => {
      setIsProcessing(true)
      setProcessingError(null)

      try {
        const items: ProcessedFileItem[] = []
        let itemIndex = 0

        for (const canvasItem of canvasItems) {
          // Check if this is a tar.gz file based on s3FilePath (title never has extension)
          if (isCanvasItemTarGz(canvasItem) && canvasItem.s3FilePath) {
            try {
              // Download the tar.gz file
              const base64Content = await getCanvasFileContent(
                currentWorkspace.uuid,
                canvasItem.s3FilePath
              )

              // Get base directory name from title (title is display name without extension)
              const baseName = canvasItem.title || 'archive'

              // Extract the tar.gz contents
              const extractedFiles = await extractTarGz(base64Content, baseName)

              // Add each extracted file as a processed item
              for (const extractedFile of extractedFiles) {
                items.push({
                  filePath: extractedFile.filePath,
                  extractedFile,
                  index: itemIndex++
                })
              }
            } catch (error) {
              // Fall back to showing the tar.gz as a single file with error stored
              items.push({
                filePath: canvasItem.title || `archive-${itemIndex}`,
                canvasItem,
                index: itemIndex++,
                extractionError:
                  error instanceof Error ? error.message : 'Failed to extract archive'
              })
            }
          } else {
            // Non-tar.gz file, add as-is
            items.push({
              filePath: canvasItem.title || `file-${itemIndex}`,
              canvasItem,
              index: itemIndex++
            })
          }
        }

        // Sort items alphabetically by file path
        items.sort((a, b) => a.filePath.localeCompare(b.filePath))

        // Re-assign indices after sorting
        items.forEach((item, idx) => {
          item.index = idx
        })

        setProcessedItems(items)
      } catch (error) {
        console.error('Failed to process canvas items:', error)
        setProcessingError(error instanceof Error ? error.message : 'Failed to process files')
      } finally {
        setIsProcessing(false)
      }
    }

    processCanvasItems()
  }, [canvasItems, currentWorkspace?.uuid])

  // Convert processed items to FileTree format
  const files: FileItem[] = useMemo(() => {
    return processedItems.map(item => ({
      filePath: item.filePath,
      data: item
    }))
  }, [processedItems])

  const selectedItem = processedItems[selectedIndex]

  // Fetch file content when selection changes
  useEffect(() => {
    if (!selectedItem) return

    const fetchContent = async () => {
      setFileContent({ content: '', isLoading: true })

      try {
        // Check if this file had an extraction error
        if (selectedItem.extractionError) {
          setFileContent({
            content: '',
            isLoading: false,
            error: `Archive extraction failed: ${selectedItem.extractionError}`
          })
          return
        }

        let content: string
        let viewerItem: FileViewerItem

        // Check if this is an extracted file (from tar.gz)
        if (selectedItem.extractedFile) {
          const extracted = selectedItem.extractedFile
          content = extracted.content

          // Create FileViewerItem from extracted file
          const filename = extracted.filePath.split('/').pop() || 'untitled'
          const size = extracted.isBase64 ? estimateBase64Size(content) : new Blob([content]).size

          viewerItem = {
            filename,
            content,
            contentEncoding: extracted.isBase64 ? 'base64' : 'text',
            mimeType: extracted.mimeType,
            size,
            isTooLarge: size > MAX_RENDERABLE_SIZE
          }
        } else if (selectedItem.canvasItem) {
          // Original canvas item (non-extracted)
          const canvasItem = selectedItem.canvasItem

          // Content source based on canvas type:
          // - CANVAS_TYPE_IMAGE: inline in base64EncodedImage
          // - CANVAS_TYPE_MARKDOWN: inline in content
          // - CANVAS_TYPE_S3_FILE: fetch from S3 using s3FilePath
          switch (canvasItem.canvasType) {
            case 'CANVAS_TYPE_IMAGE':
              content = canvasItem.base64EncodedImage || ''
              break

            case 'CANVAS_TYPE_MARKDOWN':
              content = canvasItem.content || ''
              break

            case 'CANVAS_TYPE_S3_FILE':
            default:
              if (canvasItem.s3FilePath && currentWorkspace?.uuid) {
                content = await getCanvasFileContent(currentWorkspace.uuid, canvasItem.s3FilePath)
              } else {
                setFileContent({
                  content: '',
                  isLoading: false,
                  error: canvasItem.s3FilePath ? 'Workspace not available' : 'No S3 path for file'
                })
                return
              }
              break
          }

          // Normalize to FileViewerItem for consistent handling
          viewerItem = normalizeToFileViewerItem(canvasItem, content)
        } else {
          setFileContent({
            content: '',
            isLoading: false,
            error: 'Invalid file item'
          })
          return
        }

        setFileContent({
          content,
          isLoading: false,
          viewerItem
        })
      } catch (error) {
        setFileContent({
          content: '',
          isLoading: false,
          error: error instanceof Error ? error.message : 'Failed to load content'
        })
      }
    }

    fetchContent()
  }, [selectedItem, currentWorkspace?.uuid])

  // Update selected path when index changes
  useEffect(() => {
    if (files[selectedIndex]) {
      setSelectedPath(files[selectedIndex].filePath)
    }
  }, [selectedIndex, files])

  // Always default new HTML files to the live rendered view rather than
  // carrying over the previously selected file's view-source toggle.
  useEffect(() => {
    setHtmlViewMode('rendered')
  }, [selectedIndex])

  useEffect(() => {
    if (!isDownloadOpen) return
    const closeDownloadDropdown = () => setIsDownloadOpen(false)
    const handlePointerDownOutside = (event: PointerEvent) => {
      const target = event.target
      if (target instanceof Node && downloadRef.current?.contains(target)) return
      closeDownloadDropdown()
    }
    document.addEventListener('pointerdown', handlePointerDownOutside, true)
    window.addEventListener('blur', closeDownloadDropdown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDownOutside, true)
      window.removeEventListener('blur', closeDownloadDropdown)
    }
  }, [isDownloadOpen])

  const handleFileSelect = useCallback((path: string, data?: unknown) => {
    const itemData = data as ProcessedFileItem | undefined
    if (itemData !== undefined) {
      setSelectedIndex(itemData.index)
      setSelectedPath(path)
    }
  }, [])

  const handlePrevFile = () => {
    if (selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1)
    }
  }

  const handleNextFile = () => {
    if (selectedIndex < processedItems.length - 1) {
      setSelectedIndex(selectedIndex + 1)
    }
  }

  // Resize handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizing(true)
  }, [])

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !sidebarRef.current) return

      const containerRect = sidebarRef.current.parentElement?.getBoundingClientRect()
      if (!containerRect) return

      const newWidth = e.clientX - containerRect.left
      const clampedWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, newWidth))
      setSidebarWidth(clampedWidth)
    },
    [isResizing]
  )

  const handleMouseUp = useCallback(() => {
    setIsResizing(false)
  }, [])

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    } else {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
  }, [isResizing, handleMouseMove, handleMouseUp])

  // Download all files as a zip (including extracted tar.gz contents)
  const downloadAllFiles = async () => {
    if (processedItems.length === 0) return

    setIsDownloadOpen(false)

    try {
      const zip = new JSZip()

      for (const item of processedItems) {
        if (item.extractedFile) {
          // Extracted file from tar.gz
          const extracted = item.extractedFile
          if (extracted.isBase64) {
            const blob = base64ToBlob(extracted.content, extracted.mimeType)
            zip.file(item.filePath, blob)
          } else {
            zip.file(item.filePath, extracted.content)
          }
        } else if (item.canvasItem) {
          // Original canvas item
          const canvasItem = item.canvasItem
          let content: string

          // Content source based on canvas type:
          // - CANVAS_TYPE_IMAGE: inline in base64EncodedImage
          // - CANVAS_TYPE_MARKDOWN: inline in content
          // - CANVAS_TYPE_S3_FILE: fetch from S3
          switch (canvasItem.canvasType) {
            case 'CANVAS_TYPE_IMAGE':
              content = canvasItem.base64EncodedImage || ''
              break
            case 'CANVAS_TYPE_MARKDOWN':
              content = canvasItem.content || ''
              break
            case 'CANVAS_TYPE_S3_FILE':
            default:
              if (canvasItem.s3FilePath && currentWorkspace?.uuid) {
                content = await getCanvasFileContent(currentWorkspace.uuid, canvasItem.s3FilePath)
              } else {
                content = canvasItem.content || ''
              }
              break
          }

          // Normalize to FileViewerItem - handles filename, extension, mimeType, encoding
          const viewerItem = normalizeToFileViewerItem(canvasItem, content)

          if (viewerItem.contentEncoding === 'base64') {
            // Convert base64 to blob for binary files
            const blob = base64ToBlob(viewerItem.content, viewerItem.mimeType)
            zip.file(viewerItem.filename, blob)
          } else {
            // Text content as-is
            zip.file(viewerItem.filename, viewerItem.content)
          }
        }
      }

      const zipContent = await zip.generateAsync({ type: 'blob' })
      const url = window.URL.createObjectURL(zipContent)
      const link = document.createElement('a')
      link.href = url
      link.download = buildCanvasDownloadFilename('canvas-files', { extension: 'zip' })
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to download files:', error)
      BetterToast.error('Failed to download files')
    }
  }

  const getHtmlPreviewRenderWidth = () => {
    const width = htmlPreviewFrameRef.current?.getBoundingClientRect().width
    if (width && Number.isFinite(width)) {
      lastHtmlPreviewWidthRef.current = width
      return width
    }
    return lastHtmlPreviewWidthRef.current
  }

  const handleDownloadRenderedPdf = async () => {
    const viewerItem = fileContent.viewerItem
    if (!viewerItem || !isHtmlMimeType(viewerItem.mimeType)) return

    setIsDownloadOpen(false)
    setIsGeneratingPdf(true)
    try {
      await downloadRenderedCanvasHtmlPdf(viewerItem.content, {
        filename: viewerItem.filename,
        renderWidth: getHtmlPreviewRenderWidth()
      })
    } catch (error) {
      console.error('Failed to generate rendered HTML PDF:', error)
      BetterToast.error('Failed to generate PDF')
    } finally {
      setIsGeneratingPdf(false)
    }
  }

  const handleOpenHtmlInNewTab = () => {
    const viewerItem = fileContent.viewerItem
    if (!viewerItem || !isHtmlMimeType(viewerItem.mimeType)) return

    try {
      if (
        !openSandboxedCanvasHtmlInNewTab(viewerItem.content, {
          onDownloadPdf: async () => {
            await downloadRenderedCanvasHtmlPdf(viewerItem.content, {
              filename: viewerItem.filename,
              renderWidth: getHtmlPreviewRenderWidth()
            })
          }
        })
      ) {
        BetterToast.error(
          'Popup blocked. Allow popups for this site to open the canvas in a new tab.'
        )
      }
    } catch (error) {
      console.error('Failed to open HTML in new tab:', error)
      BetterToast.error('Could not open the HTML in a new tab.')
    }
  }

  const renderHtmlViewToggle = () => (
    <SegmentControls
      data-testid='html-view-toggle'
      aria-label='HTML view mode'
      value={htmlViewMode}
      onValueChange={value => setHtmlViewMode(value as 'rendered' | 'source')}
    >
      <SegmentControlsList
        size='sm'
        variant='white'
        className='border-tokens-neutral-border-1 bg-tokens-neutral-fill-fill-4'
      >
        <SegmentControlsTrigger value='rendered' className='min-w-[80px]'>
          Rendered
        </SegmentControlsTrigger>
        <SegmentControlsTrigger value='source' className='min-w-[68px]'>
          Source
        </SegmentControlsTrigger>
      </SegmentControlsList>
    </SegmentControls>
  )

  const renderContent = () => {
    // Show processing state
    if (isProcessing) {
      return (
        <div className='flex items-center justify-center min-h-full'>
          <div className='text-tokens-neutral-text-tertiary'>Extracting files...</div>
        </div>
      )
    }

    if (processingError) {
      return (
        <div className='flex items-center justify-center min-h-full'>
          <div className='text-tokens-status-error-1'>{processingError}</div>
        </div>
      )
    }

    if (fileContent.isLoading) {
      return (
        <div className='flex items-center justify-center min-h-full'>
          <div className='text-tokens-neutral-text-tertiary select-none'>Loading...</div>
        </div>
      )
    }

    if (fileContent.error) {
      return (
        <div className='flex items-center justify-center min-h-full'>
          <div className='text-tokens-status-error-1'>{fileContent.error}</div>
        </div>
      )
    }

    if (!selectedItem || !fileContent.viewerItem) {
      return (
        <div className='flex items-center justify-center min-h-full'>
          <div className='text-tokens-neutral-text-tertiary'>Select a file to view</div>
        </div>
      )
    }

    const { viewerItem } = fileContent

    // Handle files that are too large to render
    if (viewerItem.isTooLarge) {
      const sizeInMB = ((viewerItem.size || 0) / (1024 * 1024)).toFixed(2)
      return (
        <div className='flex flex-col items-center justify-center min-h-full gap-4'>
          <div className='text-tokens-neutral-text-tertiary'>
            File too large to preview ({sizeInMB} MB)
          </div>
          <div className='text-tokens-neutral-text-quaternary text-sm'>
            Use the Download button to save this file
          </div>
        </div>
      )
    }

    // Handle binary files that can't be previewed (not images)
    // Check if content encoding is base64 but mime type is not previewable
    const isNonPreviewableBinary =
      viewerItem.contentEncoding === 'base64' &&
      !isImageMimeType(viewerItem.mimeType) &&
      !isMarkdownMimeType(viewerItem.mimeType)

    if (isNonPreviewableBinary) {
      return (
        <div className='flex flex-col items-center justify-center min-h-full gap-4'>
          <div className='text-tokens-neutral-text-tertiary'>Binary file cannot be previewed</div>
          <div className='text-tokens-neutral-text-quaternary text-sm'>{viewerItem.filename}</div>
          <div className='text-tokens-neutral-text-quaternary text-sm'>
            Use the Download button to save this file
          </div>
        </div>
      )
    }

    // Render based on mime type
    if (isImageMimeType(viewerItem.mimeType)) {
      const imageSrc = createDataUri(viewerItem.content, viewerItem.mimeType)

      return (
        <div className='p-6 flex items-center justify-center min-h-full'>
          <div className='bg-white p-4 rounded-lg shadow-sm'>
            <img
              src={imageSrc}
              alt={viewerItem.filename}
              className='max-w-full max-h-[700px] object-contain'
              onError={e => {
                const target = e.target as HTMLImageElement
                target.style.display = 'none'
                const errorMsg = document.createElement('div')
                errorMsg.className = 'text-tokens-status-error-1 p-4'
                errorMsg.textContent = 'Failed to load image'
                target.parentElement?.appendChild(errorMsg)
              }}
            />
          </div>
        </div>
      )
    }

    if (isMarkdownMimeType(viewerItem.mimeType)) {
      return (
        <div className='p-6 prose prose-gray max-w-none'>
          <DocMarkdown text={viewerItem.content} fontSize='sm' className='max-w-none' />
        </div>
      )
    }

    // Self-contained HTML payloads from the agent are rendered live in a
    // sandboxed iframe so users can interact with them. We deliberately omit
    // `allow-same-origin`: combined with `allow-scripts` it would let the page
    // reach `parent.document` and read auth state. Without it the iframe runs
    // in a unique opaque origin and cannot escape the sandbox.
    if (isHtmlMimeType(viewerItem.mimeType) && htmlViewMode === 'rendered') {
      return (
        <iframe
          ref={htmlPreviewFrameRef}
          title={viewerItem.filename}
          data-testid='html-canvas-iframe'
          srcDoc={viewerItem.content}
          sandbox={CANVAS_HTML_SANDBOX}
          referrerPolicy='no-referrer'
          className='w-full h-full border-0 bg-white'
        />
      )
    }

    // Plain text/code with syntax highlighting
    const language = getLanguageFromFilename(viewerItem.filename)
    const lines = viewerItem.content.split('\n')

    return (
      <div className='p-4 font-mono text-sm flex'>
        {/* Line numbers column */}
        <div className='pr-4 text-tokens-neutral-text-tertiary select-none border-r border-tokens-neutral-border-1 mr-4 flex-shrink-0'>
          {lines.map((_, index) => (
            <div key={index} className='text-right text-xs leading-[1.7142857] min-h-[1.7142857em]'>
              {index + 1}
            </div>
          ))}
        </div>
        {/* Syntax highlighted code */}
        <div className='flex-1 overflow-x-auto'>
          <HighlightedCode code={viewerItem.content} language={language} />
        </div>
      </div>
    )
  }

  const isHtmlFileSelected = Boolean(
    fileContent.viewerItem && isHtmlMimeType(fileContent.viewerItem.mimeType)
  )
  const selectedFileTitle = selectedItem?.filePath || 'Select a file'
  const handleHeaderDownloadClick = () => {
    if (isHtmlFileSelected) {
      setIsDownloadOpen(prev => !prev)
      return
    }

    void downloadAllFiles()
  }

  return (
    <div
      className={[
        'flex flex-col bg-white overflow-hidden',
        className ??
          'w-[1400px] h-[900px] rounded-[28px] shadow-[0px_6px_16px_0px_rgba(0,0,0,0.08),0px_3px_6px_-4px_rgba(0,0,0,0.12),0px_9px_28px_8px_rgba(0,0,0,0.05)]'
      ].join(' ')}
    >
      {/* Main Content Area with Sidebar */}
      <div className='flex flex-1 min-h-0 overflow-hidden'>
        {/* File List Sidebar - independent scrolling */}
        <div
          ref={sidebarRef}
          className='flex-shrink-0 bg-tokens-neutral-bg-grey-2 flex flex-col min-h-0'
          style={{ width: `${sidebarWidth}px` }}
        >
          {/* Sidebar Header - fixed */}
          <div className='h-[80px] border-b border-tokens-neutral-border-2 flex items-center px-6 flex-shrink-0'>
            <span className='font-geist font-semibold text-lg text-tokens-neutral-text-primary'>
              {sidebarTitle}
            </span>
          </div>

          {/* File Tree - scrollable */}
          <div className='flex-1 min-h-0 overflow-y-auto py-2'>
            <FileTree
              files={files}
              selectedPath={selectedPath}
              onFileSelect={handleFileSelect}
              emptyMessage='No files to display'
            />
          </div>
        </div>

        {/* Resize Handle */}
        <div
          className='w-2 flex-shrink-0 cursor-col-resize hover:bg-tokens-neutral-border-colored transition-colors relative group'
          onMouseDown={handleMouseDown}
        >
          <div className='absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-tokens-neutral-border-1 group-hover:bg-tokens-primary-2' />
        </div>

        {/* Main Content Column */}
        <div className='flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden'>
          {/* Content Header - fixed */}
          <div className='h-[80px] border-b border-tokens-neutral-border-2 flex items-center justify-between px-6 py-2 flex-shrink-0 bg-white'>
            <HoverText content={selectedFileTitle} className='flex-1'>
              <p className='font-geist font-medium text-base leading-[22px] text-tokens-neutral-text-primary truncate'>
                {selectedFileTitle}
              </p>
            </HoverText>
            <div className='flex items-center gap-2 flex-shrink-0 ml-4'>
              {/* Open the live HTML render from either tab. */}
              {fileContent.viewerItem && isHtmlMimeType(fileContent.viewerItem.mimeType) && (
                <button
                  onClick={handleOpenHtmlInNewTab}
                  data-testid='html-open-in-new-tab'
                  className='h-8 px-3 py-2 border border-tokens-neutral-border-1 rounded-xl flex items-center gap-2 hover:bg-tokens-neutral-fill-fill-6 transition-colors'
                  aria-label='Open Render in New Tab'
                >
                  <span className='font-geist font-medium text-sm leading-[22px] text-tokens-neutral-text-primary'>
                    Open Render in New Tab
                  </span>
                </button>
              )}
              {/* HTML render/source toggle - only shown for live HTML payloads */}
              {fileContent.viewerItem &&
                isHtmlMimeType(fileContent.viewerItem.mimeType) &&
                renderHtmlViewToggle()}
              {/* Download Button */}
              <div className='relative' ref={downloadRef}>
                <DownloadProgressButton
                  type='button'
                  onClick={handleHeaderDownloadClick}
                  disabled={isGeneratingPdf}
                  isLoading={isGeneratingPdf}
                  className='h-9 px-4 py-2 border border-tokens-neutral-border-1 rounded-xl flex items-center gap-2 hover:bg-tokens-neutral-fill-fill-6 disabled:cursor-wait'
                >
                  <span className='font-geist font-medium text-sm leading-[22px] text-tokens-neutral-text-primary'>
                    Download
                  </span>
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M14 10V12.6667C14 13.0203 13.8595 13.3594 13.6095 13.6095C13.3594 13.8595 13.0203 14 12.6667 14H3.33333C2.97971 14 2.64057 13.8595 2.39052 13.6095C2.14048 13.3594 2 13.0203 2 12.6667V10'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M4.66699 6.66699L8.00033 10.0003L11.3337 6.66699'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path
                      d='M8 10V2'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </DownloadProgressButton>
                {isDownloadOpen && isHtmlFileSelected && (
                  <SelectorDropdownContent align='end'>
                    <SelectorDropdownItem
                      hasDivider
                      onClick={handleDownloadRenderedPdf}
                      disabled={isGeneratingPdf}
                      data-testid='html-download-pdf'
                    >
                      <span className='text-sm text-tokens-neutral-text-primary'>
                        {isGeneratingPdf ? 'Generating...' : 'PDF Render (.pdf)'}
                      </span>
                    </SelectorDropdownItem>
                    <SelectorDropdownItem onClick={downloadAllFiles}>
                      <span className='text-sm text-tokens-neutral-text-primary'>
                        All Files (source) (.zip)
                      </span>
                    </SelectorDropdownItem>
                  </SelectorDropdownContent>
                )}
              </div>
              {/* Close Button */}
              <button
                onClick={onClose}
                className='w-6 h-6 flex items-center justify-center hover:bg-tokens-neutral-fill-fill-6 rounded transition-colors'
                aria-label='Close'
              >
                <svg
                  width='24'
                  height='24'
                  viewBox='0 0 24 24'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M18 6L6 18M6 6l12 12'
                    stroke='currentColor'
                    strokeWidth='2'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Content Display - scrollable independently */}
          <div className='flex-1 min-h-0 bg-tokens-neutral-bg-grey-2 overflow-auto'>
            {renderContent()}
          </div>

          {/* Footer Navigation - fixed at bottom */}
          <div className='h-[72px] bg-tokens-neutral-bg-grey-1 border-t border-tokens-neutral-border-2 flex items-center justify-center px-8 py-4 flex-shrink-0'>
            <div className='flex items-center'>
              {/* Previous Button */}
              <button
                onClick={handlePrevFile}
                disabled={selectedIndex === 0}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                  selectedIndex === 0
                    ? 'cursor-not-allowed opacity-40'
                    : 'hover:bg-tokens-neutral-fill-fill-6 cursor-pointer'
                }`}
                aria-label='Previous file'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                  className='rotate-180'
                >
                  <path
                    d='M6 12L10 8L6 4'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
              {/* Next Button */}
              <button
                onClick={handleNextFile}
                disabled={selectedIndex === processedItems.length - 1}
                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors ${
                  selectedIndex === processedItems.length - 1
                    ? 'cursor-not-allowed opacity-40'
                    : 'hover:bg-tokens-neutral-fill-fill-6 cursor-pointer'
                }`}
                aria-label='Next file'
              >
                <svg
                  width='16'
                  height='16'
                  viewBox='0 0 16 16'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M6 12L10 8L6 4'
                    stroke='currentColor'
                    strokeWidth='1.5'
                    strokeLinecap='round'
                    strokeLinejoin='round'
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CanvasFileViewer
